import { Component, ViewChild, ElementRef, ChangeDetectorRef, AfterViewChecked, AfterViewInit, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from "@angular/forms";
import { AuthService } from "../_services/auth.service";
import { TokenService } from "../_services/token.service";
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './login.component.html',
})
export class LoginComponent implements OnInit, AfterViewChecked, AfterViewInit {
  msg = '';
  private _view = 'enter-email';
  private viewChanged = false;
  email = '';
  loginCode = '';
  loading = false;
  returnUrl: string = '/'; // Default redirect
  
  @ViewChild('loginCodeField') loginCodeField!: ElementRef;
  @ViewChild('emailField') emailField!: ElementRef;

  constructor(
      private router: Router,
      private route: ActivatedRoute,
      private authService: AuthService,
      private _tokenService: TokenService,
      private cdr: ChangeDetectorRef,
      private toastr: ToastrService
  ) {
    // Get the return URL from query parameters
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || this.returnUrl;
  }

  get view() {
    return this._view;
  }

  set view(value: string) {
    if (this._view !== value) {
      this._view = value;
      this.viewChanged = true;
    }
  }

  /** Button label: step-appropriate progress text while a request is in flight. */
  get submitLabel(): string {
    if (!this.loading) {
      return 'Log In';
    }
    return this._view === 'enter-email' ? 'Sending code…' : 'Verifying…';
  }

  ngOnInit() {
    // If user is already authenticated, redirect them to the intended destination
    if (this.authService.isAuthenticated()) {
      this.router.navigate([this.getReturnUrl()]);
      return;
    }
  }

  ngAfterViewInit() {
    // Focus email input when component loads
    this.emailField?.nativeElement?.focus();
  }

  ngAfterViewChecked() {
    if (this.viewChanged && this._view === 'enter-code') {
      this.loginCodeField?.nativeElement?.focus();
      this.viewChanged = false;
      this.cdr.detectChanges();
    }
  }

  loginform = true;
  recoverform = false;

  authorize(email: string, loginCode: string) {
    // Both the button and the (keyup.enter) handlers on either input land here, so without this
    // an impatient user stacks requests - on the email step that means several login-code emails,
    // and the last one invalidates the code they may already have typed.
    if (this.loading) {
      return;
    }

    if(this.view === 'enter-email'){
      this.loading = true;
      this.authService.login(email).subscribe({
        next: (res) => {
          if(res.success){
            this.view = 'enter-code';
          } else {
            // A 200 that isn't a success used to be silent, which would now also strand the
            // button disabled - report it the same way as a transport error.
            this.toastr.error(res.message || 'Failed to send login code. Please try again.', 'Error');
          }
          this.loading = false;
        },
        error: (error) => {
          const errorMessage = error.error?.message || 'Failed to send login code. Please try again.';
          this.toastr.error(errorMessage, 'Error');
          this.loading = false;
        }
      });
    } else {
        // Validate login code is not empty
        if (!loginCode || loginCode.trim() === '') {
          this.toastr.error('Please enter the login code', 'Error');
          return;
        }

        this.loading = true;
        this.authService.login(email, loginCode).subscribe({
            next: (res) => {
                if (res.success) {
                    try {
                        this._tokenService.storeToken(res.token);

                        // Pass the token to setCurrentUser so it gets attached to the user object
                        this.authService.setCurrentUser(res.currentUser, res.token);
                    } catch (err) {
                        // Both of these write to localStorage, which throws when storage is full or
                        // disabled. Without this the throw is swallowed silently and the button
                        // stays disabled on "Verifying…" forever, with no way back but a reload.
                        console.error('Failed to persist the session after login', err);
                        this.toastr.error(
                          'Logged in, but your session could not be saved. Check your browser storage settings and try again.',
                          'Error'
                        );
                        this.loginCode = '';
                        this.loading = false;
                        return;
                    }

                    this.toastr.success('Login successful!', 'Success');

                    // `loading` is deliberately left set: the redirect below tears down the
                    // component, and clearing it first flashes an enabled "Log In" button.
                    // Redirect to the originally requested URL or default
                    this.router.navigate([this.getReturnUrl()]);
                } else {
                    this.toastr.error(res.message || 'Authentication failed. Please try again.', 'Error');
                    this.loginCode = ''; // Clear the login code field, as on error
                    this.loading = false;
                }
            },
            error: (error) => {
                const errorMessage = error.error?.message || 'Authentication failed. Please try again.';
                this.toastr.error(errorMessage, 'Error');
                this.loginCode = ''; // Clear the login code field on error
                this.loading = false;
            }
        });
    }
  }

  getReturnUrl() {
    // If no specific return URL or it's the root, use role-based home
    if (this.returnUrl === '/' || !this.returnUrl) {
      return this.authService.getHomeUrl();
    }
    
    // Validate the return URL for security
    if (!this.authService.isValidReturnUrl(this.returnUrl)) {
      console.warn('Invalid return URL detected, redirecting to home:', this.returnUrl);
      return this.authService.getHomeUrl();
    }
    
    return this.returnUrl;
  }
}
